
import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { AppConfig, AppContextType, defaultImageEdits } from './types';
import { logoUrl as defaultLogo } from './assets';
import { db } from './firebase';

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};

interface AppProviderProps {
    children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
    const [appConfig, setAppConfig] = useState<AppConfig>({
        logoUrl: defaultLogo
    });
    const [puzzlesSolvedCount, setPuzzlesSolvedCount] = useState(0);

    useEffect(() => {
        if (!db) return;
        // 1. Real-time Listener
        const unsubscribe = db.collection('settings').doc('appConfig')
            .onSnapshot((doc: any) => {
                if (doc.exists) {
                    const data = doc.data() as AppConfig;
                    setAppConfig(prev => ({ 
                        ...prev, 
                        ...data,
                        logoUrl: defaultLogo // Force defaultLogo to bypass broken Firestore URL
                    }));
                }
            }, (error: any) => {
                // Silently handle permission errors to avoid console noise on Landing Page if rules are strict
                if (error.code !== 'permission-denied') {
                    console.error("Error fetching app config:", error);
                }
            });

        return () => unsubscribe();
    }, []);

    const updateAppConfig = async (newConfig: Partial<AppConfig>) => {
        try {
            if (db) await db.collection('settings').doc('appConfig').set(newConfig, { merge: true });
        } catch (error) {
            console.error("Error updating app config:", error);
        }
    };
    
    const incrementPuzzlesSolvedCount = () => {
        setPuzzlesSolvedCount(prev => prev + 1);
    };

    const resetPuzzlesSolvedCount = () => {
        setPuzzlesSolvedCount(0);
    };

    const value = {
        appConfig,
        updateAppConfig,
        puzzlesSolvedCount,
        incrementPuzzlesSolvedCount,
        resetPuzzlesSolvedCount,
    };

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
